import React, { Component } from 'react';
import PropTypes from 'prop-types';
import CONSTANTS from '../constants/constants';
import SortSelect from '../sort/sort-select';
import ItemLayoutSwitch from '../layout/item-layout-switch';
import FoldedCornerContainer from '../folded-corner-container';
import Tag from '../../tags/tag';
import SaveSearchButton from '../save-search-button';
import saveSearchDataService from '../save-search-button-data-service';
import ArrowIcon from '@tradera/tradera-ui/icons/arrow-down.svg?jsx';
import FilterIcon from '@tradera/tradera-ui/icons/filter.svg?jsx';
import ListIcon from '@tradera/tradera-ui/icons/list.svg?jsx';
import PAGE_TYPES from 'constants/page-types';
import * as gtm from '../search-gtm';
import { generateH1 } from './header';
import { ShowMore } from 'components';
import { BPOINT, breakPoints } from 'constants/breakpoints';
import { formatNumber } from 'packages';
import MediaQuery from 'react-responsive';

const renderFilterTags = (activeFilters, handleFilterChange) => {
    return activeFilters.map((filter, index) => {
        const { parameter, options } = filter;
        const selectedName = options.find(
            option => option.value === filter.selectedValue
        ).name;
        const defaultParam = options[0].value;
        return (
            <Tag
                key={index}
                text={selectedName}
                onClick={() => {
                    gtm.trackButton('Remove filter');
                    handleFilterChange(parameter, defaultParam);
                }}
            />
        );
    });
};

const renderBrandTags = (filterBrands, handleBrandChange) => {
    const { parameter, selectedValues, options = [] } = filterBrands;
    return options.map((brand, index) => {
        const selected = selectedValues.includes(brand.value);
        return (
            selected && (
                <Tag
                    key={index}
                    text={brand.name}
                    onClick={() => {
                        handleBrandChange(parameter, brand.value);
                    }}
                />
            )
        );
    });
};

const renderPriceTag = (
    userSetPriceFilter,
    filterPrice,
    handlePriceFilterChange
) => {
    const { fromPrice, toPrice } = filterPrice;
    if (userSetPriceFilter) {
        const priceText =
            toPrice.value < 5000
                ? `${fromPrice.value} - ${toPrice.value} kr`
                : `Över ${fromPrice.value} kr`;
        return (
            <Tag
                text={priceText}
                onClick={() => handlePriceFilterChange({ fromPrice, toPrice })}
            />
        );
    }
};

const mobileOnlyProps = { maxWidth: breakPoints[BPOINT.small].max };
const desktopOnlyProps = { minWidth: breakPoints[BPOINT.medium].min };

class SearchResultsHeader extends Component {
    static propTypes = {
        initData: PropTypes.object,
        queryParams: PropTypes.object,
        filters: PropTypes.object,
        filterBrands: PropTypes.object,
        filterPrice: PropTypes.object,
        itemsFound: PropTypes.number,
        headerCategory: PropTypes.object,
        handleFilterChange: PropTypes.func.isRequired,
        handleBrandChange: PropTypes.func.isRequired,
        handlePriceFilterChange: PropTypes.func.isRequired,
        handleModalClick: PropTypes.func.isRequired,
        handleClearAllFilters: PropTypes.func.isRequired,
        activeFilters: PropTypes.array,
        itemLayout: PropTypes.string,
        pageType: PropTypes.string.isRequired,
        dispatch: PropTypes.func
    };

    constructor(props) {
        super(props);
    }

    renderContentDescription = () => {
        const { headerCategory = {}, pageType } = this.props;
        const { introText } = headerCategory;
        const categoryResultPage = pageType === PAGE_TYPES.CATEGORY_RESULT_PAGE;

        return (
            introText &&
            categoryResultPage && (
                <div className="col-12 mt-4">
                    <MediaQuery {...mobileOnlyProps}>
                        <ShowMore>
                            <FoldedCornerContainer html={introText} />
                        </ShowMore>
                    </MediaQuery>
                    <MediaQuery {...desktopOnlyProps}>
                        <FoldedCornerContainer html={introText} />
                    </MediaQuery>
                </div>
            )
        );
    };

    render() {
        const {
            queryParams,
            initData,
            itemsFound,
            headerCategory,
            filters,
            filterBrands,
            filterPrice,
            handleBrandChange,
            handleFilterChange,
            handlePriceFilterChange,
            activeFilters = [],
            handleModalClick,
            handleClearAllFilters,
            itemLayout,
            dispatch,
            pageType
        } = this.props;
        const { sortBy } = filters;
        const { selectedValues: selectedBrands = [] } = filterBrands;
        const { fromPrice, toPrice } = filterPrice;
        const { categoryName } = headerCategory;
        const searchTerm = queryParams.q;
        const userSetPriceFilter = fromPrice.value > 1 || toPrice.value < 5000;
        const totalActiveFilters =
            activeFilters.length + selectedBrands.length + userSetPriceFilter;
        const headerText = generateH1(
            pageType === PAGE_TYPES.CATEGORY_RESULT_PAGE,
            searchTerm,
            categoryName,
            filterBrands
        );

        return (
            <header className="search-results-header container col-12">
                <MediaQuery {...mobileOnlyProps}>
                    <div className="row d-xs-block filters">
                        <div
                            className="col-6 p-3 bg-white justify-content-between border border-top-0 border-light"
                            onClick={() => {
                                gtm.trackAction('Category menu', 'Open');
                                handleModalClick(
                                    CONSTANTS.MODALS.CATEGORY_MODAL
                                );
                            }}>
                            <ListIcon className="icon icon-sm icon-dark d-inline-block mx-2" />
                            Kategorier
                            <span className="d-inline-block mx-2 float-right">
                                <ArrowIcon className="icon icon-sm icon-dark" />
                            </span>
                        </div>
                        <div
                            className="col-6 p-3 bg-white justify-content-between border border-top-0 border-left-0 border-light"
                            onClick={() => {
                                gtm.trackAction('Filter menu', 'Open');
                                handleModalClick(CONSTANTS.MODALS.FILTER_MODAL);
                            }}>
                            <FilterIcon className="icon icon-sm icon-dark d-inline-block mx-2" />
                            Filter
                            <span className="d-inline-block mx-2 float-right">
                                <ArrowIcon className="icon icon-sm icon-dark" />
                            </span>
                            {totalActiveFilters > 0 && (
                                <span className="d-inline-block badge badge-pill badge-primary float-right">
                                    {totalActiveFilters}
                                </span>
                            )}
                        </div>
                    </div>
                </MediaQuery>
                <div className="search-results-header__heading row justify-content-between pb-4 border-bottom">
                    <div className="col-xl-8 col-lg-7 col-md-6 col-12 d-flex align-items-center justify-content-center justify-content-md-start">
                        <h1
                            className="h4 font-weight-bold"
                            data-test-search-result-heading>
                            {headerText}
                        </h1>
                    </div>
                    <div className="col-xl-4 col-lg-5 col-md-6 col-12 d-flex justify-content-md-between justify-content-center align-items-center save-search-wrapper pt-3 pt-md-0">
                        <span className="d-md-inline-block d-none font-weight-bold mx-3">
                            Missa inget nytt:
                        </span>
                        <SaveSearchButton
                            queryParams={queryParams}
                            isLoggedIn={initData.isLoggedIn}
                            dispatch={dispatch}
                            dataService={saveSearchDataService}
                            category={headerCategory}
                            searchName={headerText}
                        />
                    </div>
                    {this.renderContentDescription()}
                </div>
                <MediaQuery {...desktopOnlyProps}>
                    <div className="d-flex justify-content-between align-items-center py-3">
                        <div className="col-auto">
                            <span>{`${formatNumber(
                                itemsFound
                            )} annonser`}</span>
                        </div>
                        <div className="d-flex justify-content-end">
                            <div className="mr-3">
                                <SortSelect
                                    sortBy={sortBy}
                                    handleFilterChange={(filterType, value) => {
                                        gtm.trackFilter(filterType, value);
                                        handleFilterChange(filterType, value);
                                    }}
                                />
                            </div>
                            <ItemLayoutSwitch
                                dispatch={dispatch}
                                selectedLayout={itemLayout}
                                id="search-result-header-itemLayout"
                            />
                        </div>
                    </div>
                </MediaQuery>
                <MediaQuery {...mobileOnlyProps}>
                    <div className="row justify-content-center py-2">
                        <span>
                            {`${formatNumber(itemsFound)} annonser`}
                            {totalActiveFilters > 0 ? ' • Filtrerat på:' : null}
                        </span>
                    </div>
                </MediaQuery>
                {totalActiveFilters > 0 && (
                    <div className="row search-results-header__active_filters d-flex flex-nowrap flex-md-wrap align-items-center pb-4">
                        <span className="d-none d-md-inline col-auto font-weight-bold">
                            Filtrerat på:{' '}
                        </span>
                        {renderFilterTags(activeFilters, handleFilterChange)}
                        {renderBrandTags(filterBrands, handleBrandChange)}
                        {renderPriceTag(
                            userSetPriceFilter,
                            filterPrice,
                            handlePriceFilterChange
                        )}
                        <button
                            className="btn btn-sm btn-link col-auto text-danger"
                            onClick={() => {
                                gtm.trackButton('Remove all filters');
                                handleClearAllFilters();
                            }}>
                            - Rensa
                        </button>
                    </div>
                )}
            </header>
        );
    }
}

export default SearchResultsHeader;



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/components/search-results/header/search-results-header.jsx